// Cognito group / role identifiers. Centralized so references stay grep-able and edits are atomic.
export const ROLE_JOURNEY_MANAGER_BETA_USER = "ROLE_journey_manager_beta_user";

// Canonical UUID shape: 8-4-4-4-12 hex chars, case-insensitive. Matches every
// customer cognito group we've seen and rejects any 36-char role string
// (e.g. a future ROLE_* identifier that happens to be 36 chars long).
// We do not validate RFC 4122 version / variant bits — the string-form check
// is enough to distinguish a customer group from any role identifier.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isCustomerCognitoGroup = (group) => typeof group === "string" && UUID_REGEX.test(group);

// Results are cached by input array identity so repeated calls (e.g. every
// getAuthHeaders() invocation reading the same redux-state groups reference)
// don't re-scan. Redux state is stable between reducer runs, so cache hits
// are the common case. WeakMap so entries GC once the source array is dropped.
const orderCache = new WeakMap();
const customerOnlyCache = new WeakMap();
const nonCustomerOnlyCache = new WeakMap();

/**
 * Orders the cognito groups so that customer cognito groups (UUIDs) come first
 * and any non-customer groups (roles like "admin", "roadmin", "beta", "internal",
 * "ROLE_api_beta_access", etc.) come last. A lot of the frontend logic depends on
 * groups[0] being a customer cognito group UUID.
 *
 * Customer cognito groups are identified by matching the canonical UUID shape
 * (8-4-4-4-12 hex, case-insensitive). Any string that doesn't match — including
 * a role identifier that happens to be 36 chars long — sorts to the back.
 *
 * @param {Array} groups - The group IDs to order.
 * @returns {Array} - The ordered group IDs (customer UUIDs first, role groups last).
 *                    Relative order within each bucket is preserved.
 */
const orderCognitoGroups = (groups) => {
    if (!groups) {
        return [];
    }
    const cached = orderCache.get(groups);
    if (cached) return cached;
    const customerGroups = groups.filter(isCustomerCognitoGroup);
    const nonCustomerGroups = groups.filter(group => !isCustomerCognitoGroup(group));
    const result = [...customerGroups, ...nonCustomerGroups];
    orderCache.set(groups, result);
    return result;
};

/**
 * Extracts the customer cognito groups from a groups array. A "customer group"
 * is a string matching the canonical UUID shape (8-4-4-4-12 hex,
 * case-insensitive) — anything else (role identifiers, 36-char non-UUID
 * strings, null/undefined entries) is filtered out.
 *
 * @param {Array} groups - The cognito groups.
 * @returns {Array} - The customer groups.
 */
const getOnlyCustomerCognitoGroups = (groups) => {
    if (!groups) {
        return [];
    }
    const cached = customerOnlyCache.get(groups);
    if (cached) return cached;
    const result = groups.filter(isCustomerCognitoGroup);
    customerOnlyCache.set(groups, result);
    return result;
}

/**
 * Structural complement of getOnlyCustomerCognitoGroups: returns every entry
 * that is NOT a customer cognito group. Includes role identifiers (admin,
 * roadmin, beta, internal, ROLE_*), 36-char non-UUID strings, and any other
 * non-UUID entry (excluding null / undefined, which are dropped).
 *
 * @param {Array} groups - The cognito groups.
 * @returns {Array} - The non-customer groups (roles), original order preserved.
 */
const getOnlyNonCustomerCognitoGroups = (groups) => {
    if (!groups) {
        return [];
    }
    const cached = nonCustomerOnlyCache.get(groups);
    if (cached) return cached;
    const result = groups.filter(group => typeof group === "string" && !isCustomerCognitoGroup(group));
    nonCustomerOnlyCache.set(groups, result);
    return result;
}

/**
 * Returns whether the access token has cognito groups.
 * @param {String} accessToken 
 * @returns {Boolean} - True if the access token has cognito groups, false otherwise.
 */
const accessTokenHasCognitoGroups = (accessToken) => {
    return accessToken && accessToken.payload && accessToken.payload['cognito:groups'];
}

/**
 * Checks if the user's cognito groups include the given group.
 * @param {Array} userCognitoGroups - The user's cognito groups.
 * @param {String} group - The group to check.
 * @returns {Boolean} - True if the user's cognito groups include the given group, false otherwise.
 */
const userCognitoGroupsInclude = (userCognitoGroups, group) => {
    if (!userCognitoGroups || !group) {
        return false;
    }
    return userCognitoGroups.includes(group);
}

/**
 * Gets the cognito groups from the access token.
 * @param {String} accessToken - The access token.
 * @returns {Array} - The cognito groups.
 */
const getCognitoGroupsFromAccessToken = (accessToken) => {
    if (accessTokenHasCognitoGroups(accessToken)) {
        let groups = accessToken.payload['cognito:groups'];
        return orderCognitoGroups(groups);
    }
    return [];
}

/**
 * Returns the current customer cognito group UUID for the user (the first
 * customer group). Use this anywhere you previously read groups[0] expecting
 * a customer/account ID — it is robust against role groups (admin, roadmin,
 * beta, internal, ROLE_*, etc.) appearing in the user's cognito groups.
 *
 * @param {Array} groups - The user's cognito groups.
 * @returns {String|null} - The current customer cognito group UUID, or null if none.
 */
const getCurrentCustomerId = (groups) => {
    return getOnlyCustomerCognitoGroups(groups)[0] || null;
};

export const CurrentUserSessionHelper = {
    orderCognitoGroups,
    getOnlyCustomerCognitoGroups,
    getOnlyNonCustomerCognitoGroups,
    accessTokenHasCognitoGroups,
    userCognitoGroupsInclude,
    getCognitoGroupsFromAccessToken,
    getCurrentCustomerId
};